import { useCallback, useMemo } from 'react';

import { PlayUserType } from '@genially/p2p-lib';
import { getPlayPlayerSessionJoinedEvent, SnowplowWrapper } from '@genially/snowplow';
import type { PlayContext } from '@genially/snowplow-contracts';
import config from '@genially/ui/src/config/config';
import { useEffectOnce } from '@genially/ui/src/hooks/useEffectOnce';
import { cookiePermissions } from '@genially/ui/src/utils/CookiePermissions';
import { calculate } from '@genially/utils/build/module';

import { useAuthenticatedUser } from '../../../bootstrap/application/components/app/components/AuthenticatedUserProvider/AuthenticatedUserProvider.context';
import { usePlaySessionInfo } from '../../../bootstrap/application/components/app/components/PlaySessionInfoProvider/PlaySessionInfoProvider.context';
import { getLogger } from '../../infrastructure/logger';

const snowplow = new SnowplowWrapper(
  'playTracker',
  config.isProduction,
  cookiePermissions,
  config.snowplow.collectorUrl,
  config.snowplow.appId,
  config.debug,
);

const getUserTypeFromPath = () => {
  const path = window.location.pathname;

  return path.startsWith('/p') ? PlayUserType.Player : PlayUserType.Host;
};

export const useInitSnowplow = () => {
  const { authenticatedUser } = useAuthenticatedUser();

  useEffectOnce(() => {
    snowplow.initTracker({ userId: authenticatedUser?.id });
  });
};

export const useSnowplowService = () => {
  const { authenticatedUser } = useAuthenticatedUser();
  const playSessionInfo = usePlaySessionInfo();

  const play = useMemo<PlayContext | undefined>(() => {
    const { id: userId, role, sector } = authenticatedUser ?? ({} as any);

    const { alias, sessionId, teamId, geniallyId } = calculate(() => {
      if (!playSessionInfo.isActive) return {};

      const { user } = playSessionInfo;

      return {
        geniallyId: user.geniallyId,
        sessionId: user.sessionId,
        alias: user.type === PlayUserType.Player ? user.username : undefined,
        teamId: user.type === PlayUserType.Host ? user.teamId : undefined,
      };
    });

    const type = calculate(() => {
      if (playSessionInfo.isActive) {
        return playSessionInfo.user.type === PlayUserType.Host ? 'host' : 'player';
      }

      return getUserTypeFromPath() === PlayUserType.Host ? 'host' : 'player';
    });

    return {
      type,
      user_id: userId,
      role,
      sector,
      play_session_id: sessionId,
      team_id: teamId,
      genially_id: geniallyId,
      alias,
    } satisfies PlayContext;
  }, [authenticatedUser, playSessionInfo]);

  return {
    sendPageView: useCallback(async () => {
      snowplow.sendPageViewEvent({
        play,
      });
    }, [play]),
    sendPlayerSessionJoinedEvent: useCallback(
      async (place: 'lobby' | 'in-game') => {
        if (!play) {
          getLogger().warn(
            'Trying to send a player session joined event without a play context',
          );
          return;
        }

        snowplow.trackEvent(getPlayPlayerSessionJoinedEvent({ play, place }));
      },
      [play],
    ),
  };
};
